import React, { useEffect, useState } from "react";
import styled from "styled-components";
import BuildTools from "./BuildTools";
import EditTools, { getPreviewUrl } from "./EditTools";
import { logHistory } from "../HistoryDrawer";
import {
  collection,
  query,
  onSnapshot,
  doc,
  deleteDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../../../firebase";

const spacing = 4;
const borderRadius = 7;
const neatMode = true;

const colors = [
  "#264653",
  "#2A9D8E",
  "#E9C46B",
  "#F3A261",
  "#E66F51",
  "#264653",
  "#2A9D8E",
  "#E9C46B",
  "#F3A261",
  "#E66F51",
];

function Branch(props) {
  const [me, setMe] = useState(false);
  const [branches, setBranches] = useState([]);
  const [collectionEmpty, setCollectionEmpty] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [info, setInfo] = useState({});

  useEffect(() => {
    if (props.deleting) {
      setDeleting(props.deleting);
      setMe(true);
    }
  }, [props.deleting]);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, props.path, props.id),
      (docSnap) => {
        if (docSnap.exists()) {
          setInfo(docSnap.data());
        }
        if (deleting) {
          unsub();
        }
      },
      (error) => {
        console.error("Error listening to branch doc:", error);
      }
    );
    return () => unsub();
  }, [props.path, props.id, deleting]);

  useEffect(() => {
    const newPath = `${props.path}/${props.id}/${props.id}`;
    const q = query(collection(db, newPath), orderBy("name"));
    const unsubscribe = onSnapshot(
      q,
      (colSnapshot) => {
        const list = [];
        if (colSnapshot.size > 0) {
          colSnapshot.forEach((docSnap) => {
            list.push(
              <div key={docSnap.id} style={neatMode ? { width: "fit-content" } : { display: "flex", flexDirection: "row", width: "fit-content" }}>
                <Branch
                  id={docSnap.id}
                  name={docSnap.id}
                  path={newPath}
                  index={props.index + 1}
                  deleting={deleting}
                  onPreview={props.onPreview}
                />
              </div>
            );
          });
          setCollectionEmpty(false);
          setBranches(list);
        } else {
          setCollectionEmpty(true);
          setBranches([]);
        }
      },
      (error) => {
        console.error("Error listening to branch collection:", error);
      }
    );

    if (deleting && collectionEmpty) {
      const deleteBranch = async () => {
        await deleteDoc(doc(db, props.path, props.id));
        await logHistory({
          branchName: props.name || props.id,
          path: props.path,
          docId: props.id,
          action: `Deleted branch "${props.name || props.id}"`,
          previousData: info || {},
          newData: {},
          changedKeys: Object.keys(info || {}),
        });
      };
      deleteBranch();
    }

    return () => unsubscribe();
  }, [props.path, props.id, props.index, deleting, collectionEmpty, props.onPreview]);

  const colorIndex = props.index % colors.length;

  return (
    <div style={{ width: "fit-content" }}>
      <Box
        id={`${props.path}_${props.id}_branch`}
        style={{
          backgroundColor: `${colors[colorIndex]}`,
          zIndex: `${props.index}`,
        }}
      >
        <Row>
          <ColumnLeft
            $active={me}
            onClick={() => {
              if (me && !deleting) {
                setMe(false);
              } else {
                setMe(true);
                if (props.onPreview) {
                  const url = getPreviewUrl(props.path, props.id);
                  props.onPreview(url);
                }
              }
            }}
          >
            <CentralBox>
              <This>{props.name}</This>
            </CentralBox>
          </ColumnLeft>

          <ColumnCenter
            id={props.colId}
            $open={me}
            style={{
              margin: me ? `${spacing}px` : "0px",
              borderRadius: `${borderRadius}px`,
              width: me ? "380px" : "0px",
              maxWidth: me ? "420px" : "0px",
              height: me ? "auto" : "0px",
              maxHeight: me ? "2500px" : "0px",
              opacity: me ? 1 : 0,
              visibility: me ? "visible" : "hidden",
              pointerEvents: me ? "auto" : "none",
            }}
          >
            <ColumnCenterInner
              style={{
                width: "380px",
                transform: me ? "translateX(0) scale(1)" : "translateX(-12px) scale(0.98)",
                opacity: me ? 1 : 0,
              }}
            >
              <EditTools
                show={me}
                spacing={spacing}
                borderRadius={borderRadius}
                info={info}
                id={props.id}
                path={props.path}
                deleting={deleting}
                onPreview={props.onPreview}
              />
            </ColumnCenterInner>
          </ColumnCenter>

          <ColumnRight>
            {branches.length !== 0 ? <div>{branches}</div> : null}
            {deleting ? null : (
              <BuildTools
                index={props.index}
                name={props.name}
                setCollectionEmpty={setCollectionEmpty}
                borderRadius={borderRadius}
                nextPath={`${props.path}/${props.id}/${props.id}`}
                id={props.id}
                path={props.path}
                color1={colors[(props.index + 0) % colors.length]}
                color2={colors[(props.index + 1) % colors.length]}
                color3={colors[(props.index + 2) % colors.length]}
                show={me}
                spacing={spacing}
              />
            )}
          </ColumnRight>

          <DeleteDocument
            $show={me && !deleting && props.name !== "root"}
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Are you sure you want to delete branch "${props.name}"?`)) {
                setDeleting(true);
              }
            }}
            title="Delete this branch"
          >
            +
          </DeleteDocument>
        </Row>
      </Box>
    </div>
  );
}

export default Branch;

const Box = styled.div`
  margin-bottom: ${spacing}px;
  margin-right: ${spacing}px;
  border-radius: ${borderRadius}px;
  transition: all 0.38s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  width: fit-content;
  display: inline-flex;
  flex-direction: column;
`;

const Row = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  color: white;
  transition: all 0.38s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ColumnLeft = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing}px 12px;
  cursor: pointer;
  transition: background-color 0.25s ease, opacity 0.25s ease, box-shadow 0.25s ease;
  align-items: center;
  justify-content: center;
  user-select: none;
  border-radius: ${borderRadius}px 0 0 ${borderRadius}px;
  background-color: ${({ $active }) => ($active ? "rgba(0, 0, 0, 0.18)" : "transparent")};
  box-shadow: ${({ $active }) => ($active ? "inset 0 0 0 1.5px rgba(255, 255, 255, 0.25)" : "none")};

  &:hover {
    background-color: ${({ $active }) => ($active ? "rgba(0, 0, 0, 0.24)" : "rgba(255, 255, 255, 0.14)")};
  }
`;

const CentralBox = styled.div`
  position: relative;
  text-align: left;
  margin: auto;
  white-space: nowrap;
`;

const This = styled.div`
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.5px;
  transition: 0.3s;
  cursor: pointer;
  user-select: none;
`;

const ColumnCenter = styled.div`
  position: relative;
  background-color: white;
  border-radius: ${borderRadius}px;
  overflow: hidden;
  box-sizing: border-box;
  flex-shrink: 0;
  interpolate-size: allow-keywords;
  transition: 
    width 0.38s cubic-bezier(0.16, 1, 0.3, 1),
    max-width 0.38s cubic-bezier(0.16, 1, 0.3, 1),
    height 0.38s cubic-bezier(0.16, 1, 0.3, 1),
    max-height 0.38s cubic-bezier(0.16, 1, 0.3, 1),
    margin 0.38s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.28s ease,
    visibility 0.38s;
`;

const ColumnCenterInner = styled.div`
  width: 380px;
  box-sizing: border-box;
  flex-shrink: 0;
  transition: 
    transform 0.38s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.28s ease;
`;

const ColumnRight = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: ${spacing}px;
  margin-left: 0px;
  cursor: pointer;
  transition: 0.3s;
  color: black;
  overflow: hidden;
`;

const DeleteDocument = styled.div`
  position: absolute;
  right: -5px;
  bottom: -4px;
  font-weight: 200;
  line-height: 28px;
  font-size: 28px;
  margin-right: 8px;
  box-sizing: border-box;
  transform: ${({ $show }) => ($show ? "rotate(45deg) scale(1)" : "rotate(0deg) scale(0.6)")};
  opacity: ${({ $show }) => ($show ? 0.75 : 0)};
  pointer-events: ${({ $show }) => ($show ? "auto" : "none")};
  color: white;
  cursor: pointer;
  transition: opacity 0.25s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), color 0.2s ease;
  user-select: none;
  &:hover {
    color: #ff4d4f;
    opacity: 1;
    transform: rotate(45deg) scale(1.15);
  }
`;
